import {
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  SortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CSSProperties } from "react";

// Types for the hook
export interface SortableItem {
  id: string | number;
  [key: string]: unknown;
}

export interface UseSortableOptions<T extends SortableItem> {
  items: T[];
  onReorder: (
    reorderedItems: T[],
    sourceIndex: number,
    destinationIndex: number,
  ) => void;
  strategy?: "vertical" | "horizontal" | "rect";
  idField?: keyof T; // Which field to use as the unique identifier
}

export interface UseSortableReturn<T extends SortableItem> {
  // DndContext props
  DndContextProps: {
    sensors: ReturnType<typeof useSensors>;
    collisionDetection: typeof closestCenter;
    onDragEnd: (event: DragEndEvent) => void;
  };
  // SortableContext props
  SortableContextProps: {
    items: (string | number)[];
    strategy: SortingStrategy;
  };
  // Ordered items for rendering
  orderedItems: T[];
}

/**
 * Reusable hook for dnd-kit sortable functionality
 *
 * @param options Configuration options for the sortable behavior
 * @returns Object containing all necessary props and helpers for sortable functionality
 *
 * @example
 * ```tsx
 * const { DndContextProps, SortableContextProps, orderedItems } = useSortableList({
 *   items: contacts,
 *   onReorder: (reorderedItems, sourceIndex, destinationIndex) => {
 *     setContacts(reorderedItems);
 *   },
 *   idField: 'contactId'
 * });
 *
 * return (
 *   <DndContext {...DndContextProps}>
 *     <SortableContext {...SortableContextProps}>
 *       {orderedItems.map((item) => (
 *         <SortableItem key={item.id} id={item.id} item={item} />
 *       ))}
 *     </SortableContext>
 *   </DndContext>
 * );
 * ```
 */
export const useSortableList = <T extends SortableItem>({
  items,
  onReorder,
  strategy = "vertical",
  idField = "id" as keyof T,
}: UseSortableOptions<T>): UseSortableReturn<T> => {
  // Always initialize sensors - this is a required hook call
  const sensors = useSensors(useSensor(PointerSensor));

  // Extract IDs from items directly - React 19 will optimize this
  // Ensure items is an array and handle empty case gracefully
  const itemIds = (items || [])
    .map((item) => item[idField] as string | number)
    .filter(Boolean);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = itemIds.indexOf(active.id);
    const newIndex = itemIds.indexOf(over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder the actual items based on the new ID order
    const reorderedItems = arrayMove([...(items || [])], oldIndex, newIndex);
    onReorder(reorderedItems, oldIndex, newIndex);
  };

  let sortingStrategy = verticalListSortingStrategy;
  if (strategy === "horizontal") {
    sortingStrategy = horizontalListSortingStrategy;
  } else if (strategy === "rect") {
    sortingStrategy = rectSortingStrategy;
  }

  return {
    DndContextProps: {
      sensors,
      collisionDetection: closestCenter,
      onDragEnd: handleDragEnd,
    },
    SortableContextProps: {
      items: itemIds,
      strategy: sortingStrategy,
    },
    orderedItems: items || [], // Use items directly since they're already in the correct order
  };
};

/**
 * Hook for individual sortable items - use this inside sortable item components
 *
 * @param id Unique identifier for the sortable item
 * @returns Sortable props and state
 *
 * @example
 * ```tsx
 * function SortableItem({ id, children }) {
 *   const { attributes, listeners, setNodeRef, style, isDragging } = useSortableItem(id);
 *
 *   return (
 *     <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
 *       {children}
 *     </div>
 *   );
 * }
 * ```
 */
export const useSortableItem = (id: string | number) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? "#f3f4f6" : undefined,
    zIndex: isDragging ? 100 : undefined,
    position: isDragging ? "relative" : undefined,
  };

  return {
    attributes,
    listeners,
    setNodeRef,
    style,
    isDragging,
  };
};
